/**
 * Page Transition System - Phase 5 Animation System (OPTIMIZED)
 * Route-based page transitions for AdminLayout using Framer Motion
 * 
 * Performance Optimizations:
 * - Memoized variants outside component
 * - Reduced animation complexity
 * - Optimized AnimatePresence mode
 * - React.memo for pure component optimization
 */

'use client';

import * as React from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  getReducedMotionVariants,
  DURATION,
  EASING,
} from './motion-variants';

/* ===== PAGE TRANSITION VARIANTS ===== */

/**
 * Slide transition variants for smooth page navigation
 */
const slideTransitionVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.SLOW,
      ease: EASING.SMOOTH,
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: DURATION.FAST,
      ease: EASING.SMOOTH,
    },
  },
};

/**
 * Fade transition variants for simple pages
 */
const fadeTransitionVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: DURATION.NORMAL,
      ease: EASING.SMOOTH,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/**
 * Scale transition variants for modal-like pages
 */
const scaleTransitionVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: DURATION.NORMAL,
      ease: EASING.EASE_OUT,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: DURATION.FAST,
    },
  },
};

/* ===== TRANSITION TYPE ===== */

export type TransitionType = 'slide' | 'fade' | 'scale';

const transitionVariantsMap = {
  slide: slideTransitionVariants,
  fade: fadeTransitionVariants,
  scale: scaleTransitionVariants,
};

/* ===== PAGE TRANSITION WRAPPER ===== */

export interface PageTransitionProps {
  children: React.ReactNode;
  type?: TransitionType;
  className?: string;
}

// Memoize component to prevent unnecessary re-renders
const PageTransitionComponent: React.FC<PageTransitionProps> = ({
  children,
  type = 'slide',
  className,
}) => {
  const location = useLocation();
  const variants = React.useMemo(
    () => getReducedMotionVariants(transitionVariantsMap[type]),
    [type]
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export const PageTransition = React.memo(PageTransitionComponent);

/* ===== INDIVIDUAL PAGE TRANSITION COMPONENTS ===== */

/**
 * Slide page transition wrapper
 */
export const SlidePageTransition: React.FC<Omit<PageTransitionProps, 'type'>> = (props) => (
  <PageTransition type="slide" {...props} />
);

/**
 * Fade page transition wrapper
 */
export const FadePageTransition: React.FC<Omit<PageTransitionProps, 'type'>> = (props) => (
  <PageTransition type="fade" {...props} />
);

/**
 * Scale page transition wrapper
 */
export const ScalePageTransition: React.FC<Omit<PageTransitionProps, 'type'>> = (props) => (
  <PageTransition type="scale" {...props} />
);

/* ===== ROUTE TRANSITION PROVIDER (MISSING COMPONENT) ===== */

export interface RouteTransitionProviderProps {
  children: React.ReactNode;
  type?: TransitionType;
  enabled?: boolean;
}

/**
 * Provider component for route transitions
 * Wraps the entire router to enable page transitions
 */
const RouteTransitionProviderComponent: React.FC<RouteTransitionProviderProps> = ({
  children,
  type = 'slide',
  enabled = true,
}) => {
  const location = useLocation();
  const variants = React.useMemo(
    () => getReducedMotionVariants(transitionVariantsMap[type]),
    [type]
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export const RouteTransitionProvider = React.memo(RouteTransitionProviderComponent);

/* ===== ROUTE CHANGE LISTENER ===== */

/**
 * Hook to detect route changes for analytics or side effects
 */
export const usePageTransition = (callback?: (pathname: string) => void) => {
  const location = useLocation();

  React.useEffect(() => {
    callback?.(location.pathname);
  }, [location.pathname, callback]);

  return location;
};
